import React, { useState, useEffect, useRef } from "react";
import "./../../assets/css/UC/MultiSelection.scss";

const MultiSelection = ({
  options = [],
  groups = [],
  enableSearch = true, // Changed default to true to enable search by default
  enableGroups = false,
  placeholder = "Select...",
  onChange,
  quickFilters = [], // New prop for quick filters
  showClearButton = true, // Option to show/hide clear button
  defaultSelected = [] // New prop for default selected options
}) => {
  const [selectedOptions, setSelectedOptions] = useState(defaultSelected);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const multiSelectRef = useRef(null);

  // Update selected options when defaultSelected changes
  useEffect(() => {
    if (defaultSelected && defaultSelected.length > 0) {
      setSelectedOptions(defaultSelected);
    }
  }, [defaultSelected]);

  const handleOptionClick = (option) => {
    if (selectedOptions.some((selected) => selected.ID === option.ID)) {
      return;
    }

    const updatedOptions = [...selectedOptions, option];
    setSelectedOptions(updatedOptions);
    if (onChange) onChange(updatedOptions);
  };

  const handleRemoveOption = (optionId) => {
    const updatedOptions = selectedOptions.filter((opt) => opt.ID !== optionId);
    setSelectedOptions(updatedOptions);
    if (onChange) onChange(updatedOptions);
  };

  const clearAllOptions = (e) => {
    e.stopPropagation();
    setSelectedOptions([]);
    if (onChange) onChange([]);
  };

  const applyQuickFilter = (filter) => {
    // Handle different types of quick filters
    if (filter.type === "single") {
      // Single option selection
      const option = options.find(opt => opt.ID === filter.optionId);
      if (option) {
        setSelectedOptions([option]);
        if (onChange) onChange([option]);
      }
    } else if (filter.type === "multiple") {
      // Multiple options selection
      const selectedOpts = filter.optionIds
        .map(id => options.find(opt => opt.ID === id))
        .filter(opt => opt); // Filter out undefined

      setSelectedOptions(selectedOpts);
      if (onChange) onChange(selectedOpts);
    } else if (filter.type === "query") {
      // Apply a search query that selects matching options
      const matchingOptions = options.filter(option =>
        option.Value.toLowerCase().includes(filter.query.toLowerCase())
      );
      setSelectedOptions(matchingOptions);
      if (onChange) onChange(matchingOptions);
    }
  };

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.Value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleDropdown = () => {
    if (!isDropdownOpen && multiSelectRef.current) {
      // Calculate position when opening dropdown
      const rect = multiSelectRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      setDropdownPosition({
        top: rect.bottom + scrollTop,
        left: rect.left + scrollLeft,
        width: rect.width
      });
    }
    
    setIsDropdownOpen(!isDropdownOpen);
    // Clear search when opening dropdown for better UX
    if (!isDropdownOpen) {
      setSearchTerm("");
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (e) => {
      if (multiSelectRef.current && !multiSelectRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  // Update dropdown position on scroll and resize
  useEffect(() => {
    if (!isDropdownOpen) return;

    const updatePosition = () => {
      if (multiSelectRef.current) {
        const rect = multiSelectRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        
        setDropdownPosition({
          top: rect.bottom + scrollTop,
          left: rect.left + scrollLeft,
          width: rect.width
        });
      }
    };

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isDropdownOpen]);

  const renderQuickFilters = () => {
    if (!quickFilters || quickFilters.length === 0) return null;

    return (
      <div className="multi-select-quick-filters">
        <div className="quick-filter-header">Quick Filters</div>
        <div className="quick-filter-options">
          {quickFilters.map((filter, index) => (
            <button
              key={index}
              className="quick-filter-btn"
              onClick={() => applyQuickFilter(filter)}
              title={filter.description || filter.label}
            >
              {filter.icon && <i className={`fa fa-${filter.icon}`}></i>}
              <span>{filter.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderOptions = () => {
    if (enableGroups && groups.length > 0) {
      return groups
        .map((group) => {
          // Get filtered options for this group
          const groupOptions = filteredOptions.filter(option => option.GroupID === group.ID);
          
          // Only render the group if it has matching options
          if (groupOptions.length === 0) {
            return null;
          }
          
          return (
            <div key={group.ID} className="multi-select-group">
              <span>{group.Name}</span>
              {groupOptions.map((option) => (
                <div
                  key={option.ID}
                  className={`multi-select-option ${selectedOptions.some((selected) => selected.ID === option.ID)
                      ? "selected"
                      : ""
                    }`}
                  onClick={() => handleOptionClick(option)}
                >
                  <i className="fa fa-check"></i>
                  <span>{option.Value}</span>
                </div>
              ))}
            </div>
          );
        })
        .filter(group => group !== null); // Remove null groups
    }

    return filteredOptions.map((option) => (
      <div
        key={option.ID}
        className={`multi-select-option ${selectedOptions.some((selected) => selected.ID === option.ID)
            ? "selected"
            : ""
          }`}
        onClick={() => handleOptionClick(option)}
      >
        <i className="fa fa-check"></i>
        <span>{option.Value}</span>
      </div>
    ));
  };

  return (
    <div className="multi-select" ref={multiSelectRef}>
      <div className="multi-select-header" onClick={toggleDropdown}>
        {selectedOptions.length > 0 ? (
          <>
            <div className="selected-options">
              {selectedOptions.map((option) => (
                <span key={option.ID} className="multi-select-item">
                  <span className="name">{option.Value}</span>
                  <i
                    className="fa fa-times-circle remove-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveOption(option.ID);
                    }}
                  ></i>
                </span>
              ))}
            </div>
            {showClearButton && selectedOptions.length > 0 && (
              <div className="clear-selection" onClick={clearAllOptions}>
                <i className="fa fa-times"></i>
              </div>
            )}
          </>
        ) : (
          <span className="placeholder">{placeholder}</span>
        )}
      </div>

      {isDropdownOpen && (
        <div 
          className="multi-select-dropdown"
          style={{
            
            width: `${dropdownPosition.width}px`
          }}
        >
          {/* Search input added above list options */}
          <div className="search-input">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            {searchTerm && (
              <span className="clear-search" onClick={() => setSearchTerm("")}>
                <i className="fa fa-times"></i>
              </span>
            )}
          </div>

          {/* Quick filters section */}
          {renderQuickFilters()}

          <div className="list-options">
            {filteredOptions.length === 0 ? (
              <div className="no-options">No matching options</div>
            ) : (
              renderOptions()
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelection;